import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { ArrowRight, KeyRound, Users, DollarSign, Bot, Clock } from 'lucide-react'
import type { AsistanData } from '@/lib/asistan'
import { GAUTO_API } from '@/lib/gauto-endpoints'
import { gautoRequest, type GautoSummary } from '@/lib/gauto'

const PROJECTS = [
  { id: 'signedwall', name: 'SignedWall' },
  { id: 'streamviewerbot', name: 'StreamViewerBot' },
  { id: 'asistan', name: 'Asistan' },
] as const

interface ProjectStats {
  users: number
  revenue: number
  assistants?: number
  usedMinutes?: number
}

interface ProjectData {
  users?: any[]
  transactions?: any[]
}

export default function Home() {
  const [stats, setStats] = useState<Record<string, ProjectStats>>({})
  const [gauto, setGauto] = useState<GautoSummary | null>(null)

  useEffect(() => {
    const fetchGauto = async () => {
      try {
        setGauto(await gautoRequest<GautoSummary>(GAUTO_API.summary))
      } catch (error) {
        // The card still links through so the GAUTO page can explain the failure itself.
        console.error('Failed to fetch GAUTO summary', error)
      }
    }

    fetchGauto()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      const newStats: Record<string, ProjectStats> = {}
      
      await Promise.all(PROJECTS.map(async (project) => {
        try {
          const response = await fetch(`/api/project/${project.id}`)
          if (!response.ok) {
            console.error(`Failed to fetch stats for ${project.id}: ${response.status}`)
            return
          }

          if (project.id === 'asistan') {
            const data = await response.json() as AsistanData
            newStats[project.id] = {
              users: data.users.active,
              revenue: 0,
              assistants: data.assistants.total,
              usedMinutes: data.usage.totalMinutes,
            }
            return
          }

          const data = await response.json() as ProjectData
          const userCount = data.users?.length || 0
          const revenue = data.transactions?.reduce((acc: number, tx: any) => {
            let val = 0
            
            if (tx.amount_cents !== undefined) {
              val = (typeof tx.amount_cents === 'number' ? tx.amount_cents : parseFloat(tx.amount_cents) || 0) / 100
            } else {
              const amount = tx.amount || tx.price || tx.cost || tx.total || tx.value || 0
              val = typeof amount === 'number' ? amount : parseFloat(amount) || 0
            }
            
            // For signedwall, only count positive values (credits added)
            if (project.id === 'signedwall' && val < 0) {
              return acc
            }
            
            return acc + val
          }, 0) || 0
          
          newStats[project.id] = { users: userCount, revenue }
        } catch (error) {
          console.error(`Failed to fetch stats for ${project.id}`, error)
        }
      }))
      
      setStats(newStats)
    }

    fetchStats()
  }, [])

  const totalUsers = Object.values(stats).reduce((acc, curr) => acc + curr.users, 0)
  const totalRevenue = Object.values(stats).reduce((acc, curr) => acc + curr.revenue, 0)

  return (
    <div className="space-y-8">
      {/* Overall Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-medium text-gray-900">{totalUsers}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-xl font-semibold text-gray-900">Projects</h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PROJECTS.map((project) => (
          <Link
            key={project.id}
            to={`/project/${project.id}`}
            className="block p-6 bg-white rounded-lg border border-gray-200 shadow-md hover:bg-gray-50 transition-colors"
          >
            <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
              {project.name}
            </h5>
            
            <div className="mt-4 space-y-2">
              <div className="flex items-center text-gray-600">
                <Users className="h-4 w-4 mr-2" />
                <span className="text-sm">
                  {stats[project.id] ? stats[project.id].users : '-'}{' '}
                  {project.id === 'asistan' ? 'Active users' : 'Users'}
                </span>
              </div>
              {project.id === 'asistan' ? (
                <>
                  <div className="flex items-center text-gray-600">
                    <Bot className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      {stats[project.id]?.assistants ?? '-'} Assistants
                    </span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="h-4 w-4 mr-2" />
                    <span className="text-sm">
                      {stats[project.id]?.usedMinutes !== undefined
                        ? `${stats[project.id]?.usedMinutes?.toLocaleString(undefined, { maximumFractionDigits: 1 })} min used`
                        : '-'}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center text-gray-600">
                  <DollarSign className="h-4 w-4 mr-2" />
                  <span className="text-sm">
                    ${stats[project.id] ? stats[project.id].revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'} Revenue
                  </span>
                </div>
              )}
            </div>
          </Link>
        ))}

        <Link
          to="/project/gauto"
          className="block p-6 bg-white rounded-lg border border-gray-200 shadow-md hover:bg-gray-50 transition-colors"
        >
          <div className="mb-2 flex items-center justify-between">
            <h5 className="text-2xl font-bold tracking-tight text-gray-900">GAUTO</h5>
            <ArrowRight className="h-4 w-4 text-gray-300" />
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center text-gray-600">
              <KeyRound className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {gauto ? `${gauto.licences} licences · ${gauto.active} active` : 'Licences'}
              </span>
            </div>
            <div className="flex items-center text-gray-600">
              <Users className="h-4 w-4 mr-2" />
              <span className="text-sm">
                {gauto ? `${gauto.devices} devices in use` : 'Devices'}
              </span>
            </div>
            {gauto && (gauto.expiring_soon > 0 || gauto.expired > 0 || gauto.revoked > 0) && (
              <div className="flex items-center text-amber-600">
                <Clock className="h-4 w-4 mr-2" />
                <span className="text-sm">
                  {[
                    gauto.expiring_soon > 0 ? `${gauto.expiring_soon} expiring soon` : null,
                    gauto.expired > 0 ? `${gauto.expired} expired` : null,
                    gauto.revoked > 0 ? `${gauto.revoked} blocked` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>
    </div>
  )
}
